import {useTaillightsController} from '@github-ui/taillights/provider'
import type {SourceRange, SourceRect} from '@github-ui/taillights-worker/document'
import {startTransition, useEffect, useMemo, useState} from 'react'

type RectangleLayers = {
  symbolRects: readonly SourceRect[]
  controlledRects: readonly SourceRect[]
  focusedRects: readonly SourceRect[]
  anchorRects: readonly SourceRect[]
}

type RangeRequest = {
  collapsed: readonly number[]
  /** Display row of each visible source line for `collapsed`; lets a fold change move existing rects right away. */
  sourceToDisplay?: ReadonlyMap<number, number>
  blockId?: string
  enabled?: boolean
  symbolRanges?: readonly SourceRange[]
  highlightRanges?: readonly SourceRange[]
  focusedHighlightRange?: SourceRange | null
  anchorHighlightRange?: SourceRange | null
  onRenderError?: (error: Error) => void
}

const EMPTY_RANGES: readonly SourceRange[] = []
const EMPTY_RECTS: readonly SourceRect[] = []
const EMPTY_LAYERS: RectangleLayers = {symbolRects: [], controlledRects: [], focusedRects: [], anchorRects: []}

/**
 * Moves rects measured for one fold state to another without remeasuring: folds only shift whole rows, so each rect
 * moves by its line's change in display row. Rects on lines the new state hides are dropped.
 */
export function moveRects(
  rects: readonly SourceRect[],
  from: ReadonlyMap<number, number>,
  to: ReadonlyMap<number, number>,
  lineHeight: number,
) {
  return rects.flatMap(rect => {
    const before = from.get(rect.line)
    const after = to.get(rect.line)
    if (before === undefined || after === undefined) return []
    return [after === before ? rect : {...rect, y: rect.y + (after - before) * lineHeight}]
  })
}

/** Requests worker geometry for passive highlight layers and ignores results from outdated configurations. */
export function useRangeDecorations({
  collapsed,
  sourceToDisplay,
  blockId,
  enabled = true,
  symbolRanges = EMPTY_RANGES,
  highlightRanges = EMPTY_RANGES,
  focusedHighlightRange,
  anchorHighlightRange,
  onRenderError,
}: RangeRequest): RectangleLayers {
  const controller = useTaillightsController()
  const request = useMemo(
    () => ({
      controller,
      collapsed,
      sourceToDisplay,
      blockId,
      enabled,
      symbolRanges,
      highlightRanges,
      focusedHighlightRange,
      anchorHighlightRange,
    }),
    [
      controller,
      collapsed,
      sourceToDisplay,
      blockId,
      enabled,
      symbolRanges,
      highlightRanges,
      focusedHighlightRange,
      anchorHighlightRange,
    ],
  )
  const [result, setResult] = useState<{request: typeof request; rectangles: RectangleLayers}>()

  useEffect(() => {
    if (
      !controller ||
      !enabled ||
      (!symbolRanges.length && !highlightRanges.length && !focusedHighlightRange && !anchorHighlightRange)
    )
      return
    let active = true
    const rects = (ranges: readonly SourceRange[]) =>
      ranges.length ? controller.rects({ranges, collapsed, blockId}) : Promise.resolve([])

    void (async () => {
      try {
        const [symbolRects, controlledRects, focusedRects, anchorRects] = await Promise.all([
          rects(symbolRanges),
          rects(highlightRanges),
          rects(focusedHighlightRange ? [focusedHighlightRange] : EMPTY_RANGES),
          rects(anchorHighlightRange ? [anchorHighlightRange] : EMPTY_RANGES),
        ])
        if (active) {
          startTransition(() =>
            setResult({request, rectangles: {symbolRects, controlledRects, focusedRects, anchorRects}}),
          )
        }
      } catch (error) {
        if (!active) return
        const failure = error instanceof Error ? error : new Error(String(error))
        if (onRenderError) onRenderError(failure)
        else reportError(failure)
      }
    })()
    return () => {
      active = false
    }
  }, [
    controller,
    collapsed,
    blockId,
    enabled,
    symbolRanges,
    highlightRanges,
    focusedHighlightRange,
    anchorHighlightRange,
    request,
    onRenderError,
  ])

  // While new rects are measured, each layer whose ranges are unchanged keeps its rects instead of blinking out, moved
  // to their new rows when only the folds changed. Layers whose ranges changed wait for their new rects.
  return useMemo(() => {
    if (!result) return EMPTY_LAYERS
    if (result.request === request) return result.rectangles
    const previous = result.request
    if (
      previous.controller !== request.controller ||
      previous.blockId !== request.blockId ||
      previous.enabled !== request.enabled
    )
      return EMPTY_LAYERS
    const lineHeight = request.controller?.profile.lineHeight
    const {sourceToDisplay: from} = previous
    const {sourceToDisplay: to} = request
    // Rect positions follow each line's display row, so they move whenever that mapping changes.
    let move = (rects: readonly SourceRect[]) => rects
    if (from !== to) {
      if (!from || !to || !lineHeight) return EMPTY_LAYERS
      move = rects => moveRects(rects, from, to, lineHeight)
    }
    const {symbolRects, controlledRects, focusedRects, anchorRects} = result.rectangles
    return {
      symbolRects: previous.symbolRanges === request.symbolRanges ? move(symbolRects) : EMPTY_RECTS,
      controlledRects: previous.highlightRanges === request.highlightRanges ? move(controlledRects) : EMPTY_RECTS,
      focusedRects: previous.focusedHighlightRange === request.focusedHighlightRange ? move(focusedRects) : EMPTY_RECTS,
      anchorRects: previous.anchorHighlightRange === request.anchorHighlightRange ? move(anchorRects) : EMPTY_RECTS,
    }
  }, [result, request])
}
